import { Outlet } from '@tanstack/react-router'
import { Children, ComponentPropsWithoutRef, PropsWithChildren } from 'react'

import { clx } from '@/utils/clx'

export type PageProps = {
	hasOutlet?: boolean
	children: React.ReactNode
}
export const SingleColumnPage = ({ children, hasOutlet }: PageProps) => {
	return (
		<div className='flex flex-1 flex-col gap-y-3'>
			{children}
			{hasOutlet && <Outlet />}
		</div>
	)
}

/**
 * List (or tree) on the left, the selected row's detail route on the right. Unlike
 * {@link TwoColumnPage} the narrow column is the leading one, and the detail side is always the
 * router outlet — the list stays mounted while navigating between items.
 */
export const ListDetailPage = ({ children }: PropsWithChildren) => {
	return (
		<div className='flex w-full flex-col items-start gap-x-4 gap-y-3 lg:grid lg:grid-cols-[400px_minmax(0,_1fr)] xl:grid-cols-[440px_minmax(0,_1fr)]'>
			<div className='flex w-full flex-col gap-y-3'>{children}</div>
			<div className='flex w-full min-w-0 flex-col gap-y-3'>
				<Outlet />
			</div>
		</div>
	)
}

export const TwoColumnPageComponent = ({ children, hasOutlet }: PageProps) => {
	const childrenArray = Children.toArray(children)

	if (childrenArray.length !== 2) {
		throw new Error('TwoColumnPage expects exactly two children')
	}

	const [main, sidebar] = childrenArray

	return (
		<div className='flex w-full flex-col gap-y-3'>
			<div className='flex w-full flex-col items-start gap-x-4 gap-y-3 lg:grid lg:grid-cols-[minmax(0,_1fr)_300px] xl:grid-cols-[minmax(0,_1fr)_440px]'>
				<div className='flex w-full min-w-0 flex-col gap-y-3'>{main}</div>
				<div className='flex w-full flex-col gap-y-3'>{sidebar}</div>
			</div>
			{hasOutlet && <Outlet />}
		</div>
	)
}

const Main = ({ children, className, ...props }: ComponentPropsWithoutRef<'div'>) => {
	return (
		<div className={clx('flex w-full flex-col gap-y-3', className)} {...props}>
			{children}
		</div>
	)
}

const Sidebar = ({ children, className, ...props }: ComponentPropsWithoutRef<'div'>) => {
	return (
		<div
			className={clx('flex w-full max-w-[100%] flex-col gap-y-3 lg:max-w-[300px] xl:max-w-[440px]', className)}
			{...props}
		>
			{children}
		</div>
	)
}

export const TwoColumnPage = Object.assign(TwoColumnPageComponent, { Main, Sidebar })
